import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import Quill from 'quill';

var _class, _descriptor;
let QuillService = (_class = class QuillService extends Service {
  constructor(...args) {
    super(...args);
    this.instances = Object.create(null);
    /**
     * Markup of a real snow toolbar, kept so callers that defer mounting Quill can show
     * one anyway (see CfAdvancedTextarea).
     *
     * The toolbar is 97% of an editor's DOM (303 of 312 nodes) but only ~7% of the cost
     * of building one: cloning 19 toolbars as static markup takes 29ms where mounting 19
     * editors takes 433ms. Nearly all of that is Quill's own init — parchment, a
     * MutationObserver and the module wiring, per instance. So a field that isn't being
     * used can show the real toolbar and skip the editor behind it, and look no different
     * for it. This is a copy of an actual toolbar rather than a hand-written lookalike,
     * so it cannot drift from the real one.
     */
    _initializerDefineProperty(this, "toolbarMarkup", _descriptor, this);
    this._toolbarClaimed = false;
  }
  /**
   * Claim the job of rendering the first real toolbar on the page. Returns true to
   * exactly one caller, which then has to mount for real so everyone else can copy it.
   */
  claimToolbarSource() {
    if (this.toolbarMarkup || this._toolbarClaimed) {
      return false;
    }
    this._toolbarClaimed = true;
    return true;
  }

  /**
   * Snapshot a live toolbar element. Ids are stripped (they would be duplicated across
   * every copy) and the copy is marked inert: it is a picture of a toolbar, and must not
   * take focus or clicks.
   */
  captureToolbar(element) {
    if (this.toolbarMarkup || !element) {
      return;
    }
    const copy = element.cloneNode(true);
    copy.removeAttribute('id');
    copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
    copy.setAttribute('inert', '');
    copy.setAttribute('aria-hidden', 'true');
    this.toolbarMarkup = htmlSafe(copy.outerHTML);
  }

  /**
   * Register a Quill instance by name.
   *
   * Runtime guard: never silently overwrite an existing registration (would
   * orphan the previous editor and leak its event listeners).
   */
  register(name, instance) {
    if (this.instances[name]) {
      return;
    }
    this.instances[name] = instance;
  }

  /**
   * De-register a Quill instance by name.
   *
   * Only deletes when the stored instance matches the caller's, so a late
   * teardown can't clobber a freshly registered editor reusing the same name.
   *
   * @returns {void}
   */
  deregister(name, instance) {
    if (instance === undefined || this.instances[name] === instance) {
      delete this.instances[name];
    }
  }

  /**
   * Get a Quill instance by name.
   *
   * @returns {(Quill|null)}
   */
  instance(name) {
    return this.instances[name] ?? null;
  }

  /**
   * Delete text from the named editor.
   */
  deleteText(name, index, length, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].deleteText(index, length, source);
    }
    return null;
  }

  /**
   * Retrieve the contents of the named editor.
   */
  getContents(name) {
    if (this.instances[name]) {
      return this.instances[name].getContents();
    }
    return null;
  }

  /**
   * Retrieve the length of the editor contents.
   */
  getLength(name) {
    if (this.instances[name]) {
      return this.instances[name].getLength();
    }
    return null;
  }

  /**
   * Retrieve the string content of the editor.
   */
  getText(name, index = 0, length = undefined) {
    if (this.instances[name]) {
      return this.instances[name].getText(index, length);
    }
    return null;
  }

  /**
   * Insert embedded content into the named editor.
   */
  insertEmbed(name, index, type, value, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].insertEmbed(index, type, value, source);
    }
    return null;
  }

  /**
   * Insert text into the named editor.
   */
  insertText(name, ...args) {
    if (this.instances[name]) {
      return this.instances[name].insertText(...args);
    }
    return null;
  }

  /**
   * Set contents on the named editor.
   */
  setContents(name, delta, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].setContents(delta, source);
    }
    return null;
  }

  /**
   * Set text contents on the named editor.
   */
  setText(name, text, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].setText(text, source);
    }
    return null;
  }

  /**
   * Apply delta to the named editor's contents.
   */
  updateContents(name, delta, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].updateContents(delta, source);
    }
    return null;
  }

  /**
   * Format text at the user's current selection in the named editor.
   */
  format(name, formatName, value, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].format(formatName, value, source);
    }
    return null;
  }

  /**
   * Formats all lines in a given range in the named editor.
   */
  formatLine(name, ...args) {
    if (this.instances[name]) {
      return this.instances[name].formatLine(...args);
    }
    return null;
  }

  /**
   * Format text in the named editor.
   */
  formatText(name, ...args) {
    if (this.instances[name]) {
      return this.instances[name].formatText(...args);
    }
    return null;
  }

  /**
   * Retrieves common formatting of the text in the named editor.
   */
  getFormat(name, ...args) {
    if (this.instances[name]) {
      return this.instances[name].getFormat(...args);
    }
    return null;
  }

  /**
   * Removes all formatting and embeds within the given range.
   */
  removeFormat(name, index, length, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].removeFormat(index, length, source);
    }
    return null;
  }

  /**
   * Retrieves the pixel position and dimensions of a selection.
   */
  getBounds(name, index, length = 0) {
    if (this.instances[name]) {
      return this.instances[name].getBounds(index, length);
    }
    return null;
  }

  /**
   * Retrieves the user's selection range in the named editor.
   */
  getSelection(name, focus = false) {
    if (this.instances[name]) {
      return this.instances[name].getSelection(focus);
    }
    return null;
  }

  /**
   * Remove focus from the named editor.
   */
  blur(name) {
    if (this.instances[name]) {
      this.instances[name].blur();
    }
  }

  /**
   * Disable the named editor.
   */
  disable(name) {
    if (this.instances[name]) {
      this.instances[name].disable();
    }
  }

  /**
   * Enable the named editor.
   */
  enable(name) {
    if (this.instances[name]) {
      this.instances[name].enable();
    }
  }

  /**
   * Focuses the named editor.
   */
  focus(name) {
    if (this.instances[name]) {
      this.instances[name].focus();
    }
  }

  /**
   * Does the named editor have focus?
   */
  hasFocus(name) {
    if (this.instances[name]) {
      return this.instances[name].hasFocus();
    }
    return null;
  }

  /**
   * Synchronously check the named editor for user updates.
   */
  update(name, source = Quill.sources.USER) {
    if (this.instances[name]) {
      this.instances[name].update(source);
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "toolbarMarkup", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _class);

export { QuillService as default };
//# sourceMappingURL=quill.js.map
